import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { submitFeedback } from '../../api/feedbackApi';
import { showErrorToast, showSuccessToast } from '../../lib/toast';

const CATEGORIES = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature idea' },
  { value: 'general', label: 'General' },
];

const FeedbackWidget = () => {
  const location = useLocation();
  const widgetRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onPointerDown = (event) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isOpen]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await submitFeedback({
        message: trimmed,
        category,
        pageUrl: `${window.location.origin}${location.pathname}${location.search}`,
      });
      showSuccessToast('Thanks for your feedback!');
      setMessage('');
      setCategory('general');
      setIsOpen(false);
    } catch {
      showErrorToast('Could not send feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      ref={widgetRef}
      className="flex flex-col items-end gap-2 pointer-events-none"
    >
      {isOpen && (
        <div
          className="pointer-events-auto w-[min(100vw-2rem,22rem)] rounded-2xl border border-gray-100 dark:border-gray-800 bg-white/95 dark:bg-gray-950/95 backdrop-blur-md shadow-xl shadow-gray-900/10 dark:shadow-black/40 animate-in slide-in-from-bottom-2 fade-in-0 duration-200"
          role="dialog"
          aria-label="Send feedback"
        >
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-50 dark:border-gray-900">
            <p className="text-[10px] font-black uppercase tracking-widest text-gray-900 dark:text-white">
              Send feedback
            </p>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
              aria-label="Close feedback form"
            >
              <X size={16} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 space-y-3">
            <div>
              <label
                htmlFor="feedback-category"
                className="block text-[9px] font-black uppercase tracking-widest text-gray-400 mb-1.5"
              >
                Type
              </label>
              <select
                id="feedback-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/50 px-3 py-2 text-xs font-semibold text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              >
                {CATEGORIES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="feedback-message"
                className="block text-[9px] font-black uppercase tracking-widest text-gray-400 mb-1.5"
              >
                Message
              </label>
              <textarea
                id="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                maxLength={4000}
                placeholder="Your message will reach our team immediately."
                className="w-full resize-none rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/50 px-3 py-2 text-xs text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                required
              />
            </div>

            <button
              type="submit"
              disabled={!message.trim() || isSubmitting}
              className={cn(
                'w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-white transition-all',
                'bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-600/20',
                'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-emerald-600'
              )}
            >
              <Send size={14} />
              {isSubmitting ? 'Sending…' : 'Send'}
            </button>
          </form>
        </div>
      )}

      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className={cn(
          'pointer-events-auto flex items-center gap-2 rounded-full px-3.5 py-2 transition-all',
          'text-[10px] font-bold uppercase tracking-widest backdrop-blur-sm',
          'border bg-white/80 dark:bg-gray-950/80 shadow-sm',
          'active:scale-[0.98]',
          isOpen
            ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
            : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-emerald-300 dark:hover:border-emerald-700 hover:text-emerald-700 dark:hover:text-emerald-400'
        )}
        aria-label={isOpen ? 'Close feedback' : 'Open feedback'}
        aria-expanded={isOpen}
      >
        {isOpen ? <X size={18} strokeWidth={2.5} /> : <MessageCircle size={18} strokeWidth={2.5} />}
        <span>{isOpen ? 'Close' : 'Feedback'}</span>
      </button>
    </div>
  );
};

export default FeedbackWidget;
